<template>
  <div class="question-view-wrapper">
    <div
      class="question-view-container"
      :style="{ height: questionData.replyType === 'gif' && hasSearchedOnce ? '100%' : 'max-content', paddingBottom: isGifContainerInPreSearchState ? '0' : '1em' }"
    >
      <div class="question-type-icon">
        <span v-if="questionData.replyType === 'text'" :style="{ fontSize: '1.4em' }" >
          <font-awesome-icon :icon="['far', 'comment-dots']" />
        </span>
        <span v-if="questionData.replyType === 'gif'" >
          GIF
        </span>
      </div>
      <div class="question-title">
        {{ questionData.promptText }}
      </div>
      <div v-if="questionData.replyType === 'text'" class="question-type-text">
        <textarea
          class="question-input"
          v-model="answerText"
          name="answerText"
          placeholder="Your answer"
          autocomplete="off"
          autocorrect="off"
          spellcheck="false"
          autofocus
          @keydown.enter.prevent="submitAnswer"
        >
        </textarea>
        <div :class="[{ invalid: answerText.length > characterLimit }, 'character-limit-text']">
          {{ answerText.length }} / {{ characterLimit }} characters
        </div>
      </div>
      <div
        v-else
        class="question-type-gif"
        :style="{ paddingBottom: isGifContainerInPreSearchState ? '0' : '1em', overflowY: isGifContainerInPreSearchState ? 'hidden' : 'auto' }"
      >
        <div class="gif-search-container">
          <input
            v-model="gifQuery"
            class="gif-search-input"
            placeholder="Search Tenor for a GIF using a keyword or phrase"
            autocomplete="off"
            autocorrect="off"
            spellcheck="false"
            autofocus
            @keydown="clearAndFetchGifs"
          >
        </div>
        <div v-show="!hasSearchedOnce">
          <StylizedGifAnimation />
        </div>
        <div
          v-show="hasSearchedOnce"
          class="gif-display-wrapper"
          v-on:scroll="loadMoreGifs"
        >
          <div class="gif-display-container">
            <div v-show="gifUrls.length > 0" class="gif-grid-row">
              <div v-if="isSafari" class="gif-grid-column">
                <transition-group name="fade-slow">
                  <img
                    v-for="[index, gifUrlObject] in Array.from(gifUrls.entries()).filter(entry => entry[0] % 3 === 0)"
                    :key="index"
                    :class="[{ 'not-yet-loaded': index >= numGifsLoadedSync, selected: selectedGifIndex === index, unselected: selectedGifIndex > -1 && selectedGifIndex != index }, 'question-img', 'lazyload']"
                    :src="gifUrlObject.lowResGifUrl"
                    :data-src="gifUrlObject.mediumResGifUrl"
                    @click="selectGif(index)"
                    @load="incrementNumGifsLoaded"
                  />
                </transition-group>
              </div>
              <div v-else class="gif-grid-column">
                <transition-group name="fade-slow">
                  <video
                    v-for="[index, gifUrlObject] in Array.from(gifUrls.entries()).filter(entry => entry[0] % 3 === 0)"
                    :key="index"
                    :class="[{ 'not-yet-loaded': index >= numGifsLoadedSync, selected: selectedGifIndex === index, unselected: selectedGifIndex > -1 && selectedGifIndex != index }, 'question-img']"
                    :src="gifUrlObject.highResMp4Url"
                    type="video/mp4"
                    preload="metadata"
                    muted
                    autoplay
                    loop
                    playsinline
                    @click="selectGif(index)"
                    @loadedmetadata="incrementNumGifsLoaded"
                  >
                  </video>
                </transition-group>
              </div>
              <div v-if="isSafari" class="gif-grid-column">
                <transition-group name="fade-slow">
                  <img
                    v-for="[index, gifUrlObject] in Array.from(gifUrls.entries()).filter(entry => (entry[0] - 1) % 3 === 0)"
                    :key="index"
                    :class="[{ 'not-yet-loaded': index >= numGifsLoadedSync, selected: selectedGifIndex === index, unselected: selectedGifIndex > -1 && selectedGifIndex != index }, 'question-img', 'lazyload']"
                    :src="gifUrlObject.lowResGifUrl"
                    :data-src="gifUrlObject.mediumResGifUrl"
                    @click="selectGif(index)"
                    @load="incrementNumGifsLoaded"
                  />
                </transition-group>
              </div>
              <div v-else class="gif-grid-column">
                <transition-group name="fade-slow">
                  <video
                    v-for="[index, gifUrlObject] in Array.from(gifUrls.entries()).filter(entry => (entry[0] - 1) % 3 === 0)"
                    :key="index"
                    :class="[{ 'not-yet-loaded': index >= numGifsLoadedSync, selected: selectedGifIndex === index, unselected: selectedGifIndex > -1 && selectedGifIndex != index }, 'question-img']"
                    :src="gifUrlObject.highResMp4Url"
                    type="video/mp4"
                    preload="metadata"
                    muted
                    autoplay
                    loop
                    playsinline
                    @click="selectGif(index)"
                    @loadedmetadata="incrementNumGifsLoaded"
                  >
                  </video>
                </transition-group>
              </div>
              <div v-if="isSafari" class="gif-grid-column">
                <transition-group name="fade-slow">
                  <img
                    v-for="[index, gifUrlObject] in Array.from(gifUrls.entries()).filter(entry => (entry[0] - 2) % 3 === 0)"
                    :key="index"
                    :class="[{ 'not-yet-loaded': index >= numGifsLoadedSync, selected: selectedGifIndex === index, unselected: selectedGifIndex > -1 && selectedGifIndex != index }, 'question-img', 'lazyload']"
                    :src="gifUrlObject.lowResGifUrl"
                    :data-src="gifUrlObject.mediumResGifUrl"
                    @click="selectGif(index)"
                    @load="incrementNumGifsLoaded"
                  />
                </transition-group>
              </div>
              <div v-else class="gif-grid-column">
                <transition-group name="fade-slow">
                  <video
                    v-for="[index, gifUrlObject] in Array.from(gifUrls.entries()).filter(entry => (entry[0] - 2) % 3 === 0)"
                    :key="index"
                    :class="[{ 'not-yet-loaded': index >= numGifsLoadedSync, selected: selectedGifIndex === index, unselected: selectedGifIndex > -1 && selectedGifIndex != index }, 'question-img']"
                    :src="gifUrlObject.highResMp4Url"
                    type="video/mp4"
                    preload="metadata"
                    muted
                    autoplay
                    loop
                    playsinline
                    @click="selectGif(index)"
                    @loadedmetadata="incrementNumGifsLoaded"
                  >
                  </video>
                </transition-group>
              </div>
            </div>
            <div class="no-gifs-found-container" v-show="areNoGifsFound">
              No GIFs found for {{ gifQuery }}. Try something else.
            </div>
            <div class="infinite-scroll-loading-dots-container">
              <Spinner color="teal" :style="{ opacity: isLoadingMoreGifs ? 1 : 0 }" />
            </div>
          </div>
        </div>
      </div>
      <div v-show="questionData.replyType === 'text' || hasSearchedOnce" class="submit-container" >
        <transition name="pop-from-top">
          <div v-show="questionData.replyType === 'text' || selectedGifIndex > -1">
            <div
              :class="[{ inactive: cannotClickSubmitButton }, 'submit-button']"
              @click="submitAnswer"
              :disabled="submitButtonClicked"
            >
              <span v-show="!submitButtonClicked" >
                submit
              </span>
              <Spinner color="teal" v-show="submitButtonClicked" />
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import StylizedGifAnimation from '@/components/gameComponents/StylizedGifAnimation.vue';
import Spinner from '@/components/utilComponents/Spinner';
import { debounce, throttle } from 'lodash';

export default Vue.extend({
  name: 'QuestionView',
  components: {
    StylizedGifAnimation,
    Spinner
  },
  props: {
    username: String,
    questionIndex: Number,
    numQuestions: Number,
    gameState: Object,
    gameRoomcode: String,
    questionData: Object,
    isSafari: Boolean
  },
  data: () => ({
    selectedGifIndex: -1,
    submitButtonClicked: false,
    answerText: '',
    gifQuery: '',
    lastGifQuery: '',
    gifUrls: [],
    numGifsToLoad: 0,
    numGifsLoaded: 0,
    numGifsLoadedSync: 0,
    areNoGifsFound: false,
    hasSearchedOnce: false,
    isLoadingMoreGifs: false,
    tenorSearchPosition: '',
    characterLimit: 140
  }),
  computed: {
    auth0UserId: function () {
      return this.$auth.user.sub.replace(/\|/g, '_');
    },
    idToken: function () {
      return this.$auth.idToken;
    },
    isGifContainerInPreSearchState: function() {
      return this.questionData.replyType === 'gif' && !this.hasSearchedOnce;
    },
    isNotWithinCharacterLimit: function() {
      return this.answerText.length < 1 || this.answerText.length > this.characterLimit;
    },
    cannotClickSubmitButton: function() {
      const isTextResponseInvalid = this.questionData.replyType === 'text' && this.isNotWithinCharacterLimit;
      const isGifResponseInvalid = this.questionData.replyType === 'gif' && this.selectedGifIndex < 0;
      return isTextResponseInvalid || isGifResponseInvalid;
    }
  },
  methods: {
    incrementNumGifsLoaded () {
      this.numGifsLoaded += 1;
      if (this.numGifsLoaded >= this.numGifsToLoad) {
        this.isLoadingMoreGifs = false;
        this.numGifsLoadedSync = this.numGifsLoaded;
      }
    },
    submitAnswer (event) {
      if (this.cannotClickSubmitButton || this.submitButtonClicked) {
        return;
      }
      event.preventDefault();
      this.submitButtonClicked = true;
      this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/answer`, {
        auth0UserId: this.auth0UserId,
        username: this.username,
        gameRoomcode: this.gameRoomcode,
        turnIndex: this.questionData.turnIndex,
        roundIndex: this.questionData.roundIndex,
        answerId: this.questionData.answerId,
        replyType: this.questionData.replyType,
        answerText: this.answerText,
        answerLowResGifUrl: this.selectedGifIndex === -1 ? '' :  this.gifUrls[this.selectedGifIndex].lowResGifUrl,
        answerHighResGifUrl: this.selectedGifIndex === -1 ? '' :  this.gifUrls[this.selectedGifIndex].highResGifUrl,
        answerHighResMp4Url: this.selectedGifIndex === -1 ? '' :  this.gifUrls[this.selectedGifIndex].highResMp4Url
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      }).then(() => {
        this.$emit('answer-submitted');
      }).catch(error => {
        console.log('Error:', error);
        this.submitButtonClicked = false;
      });
    },
    selectGif (gifIndex) {
      this.selectedGifIndex = gifIndex;
    },
    fetchGifs (event, isInitialQuery=false) {
      event.preventDefault();
      this.isLoadingMoreGifs = true;
      this.areNoGifsFound = false;
      // load more gifs on an initial query to increase the chance the scroll bar showing up
      // NOTE: Tenor allows retrieval of at most 50 gifs / videos in one request
      let numGifsLimit = isInitialQuery ?
        Math.min(50, Math.round(1.5 * Number(process.env.VUE_APP_TENOR_NUM_GIF_RESULTS))) :
        Math.min(50, Number(process.env.VUE_APP_TENOR_NUM_GIF_RESULTS));

      if (this.lastGifQuery.length > 0) {
        this.axios.get(
          process.env.VUE_APP_TENOR_SEARCH_ENDPOINT,
          {
            params: {
              key: process.env.VUE_APP_TENOR_API_KEY,
              q: this.lastGifQuery,
              locale: process.env.VUE_APP_TENOR_LOCALE,
              contentfilter: process.env.VUE_APP_TENOR_CONTENT_FILTER,
              ar_range: process.env.VUE_APP_TENOR_ASPECT_RATIO_RANGE,
              limit: numGifsLimit,
              pos: this.tenorSearchPosition
            }
          }, {
            headers: {
              'Content-Type': 'application/json', 
              'Authorization': `Bearer ${this.idToken}`
            }
          }
        ).then(response => {
          const gifResults = response.data.results;
          this.hasSearchedOnce = true;
          if (gifResults.length === 0) {
            this.isLoadingMoreGifs = false;
            this.areNoGifsFound = this.tenorSearchPosition === '';
          } else {
            this.tenorSearchPosition = response.data.next;
            this.numGifsToLoad = this.gifUrls.length + gifResults.length;
            gifResults.forEach(gifDatum => {
              this.gifUrls.push({
                lowResGifUrl: gifDatum.media[0].nanogif.url,
                mediumResGifUrl: gifDatum.media[0].tinygif.url,
                highResGifUrl: gifDatum.media[0].mediumgif.url,
                highResMp4Url: gifDatum.media[0].mp4.url
              });
            });
          }
        }).catch(error => {
          console.log('Error:', error);
        });
      }
    },
    clearAndFetchGifs: debounce(function(event) {
      if (this.gifQuery.length > 0) {
        this.lastGifQuery = this.gifQuery;
        document.querySelector('.gif-display-wrapper').scrollTop = 0;
        // stop preloading previous gifs to prevent them from appearing on a new query
        this.gifUrls = [];
        this.numGifsToLoad = 0;
        this.numGifsLoaded = 0;
        this.numGifsLoadedSync = 0;
        this.tenorSearchPosition = '';
        this.selectedGifIndex = -1;
        this.fetchGifs(event, true);
      }
    }, 500),
    loadMoreGifs: throttle(function(event) {
      // throttle scrolling and improve performance.
      // Chrome also uses floating pixel values when zoomed in, so don't check === 0. If you need to check an exact match, use < 1.
      const element = event.target;
      const isScrollConstraintSatisfied = element.scrollHeight - element.scrollTop - element.offsetHeight < Math.min(0.25 * element.scrollHeight, 600);
      if (isScrollConstraintSatisfied && !this.isLoadingMoreGifs && !this.areNoGifsFound) {
        this.fetchGifs(event);
      }
    }, 500, { trailing: true })
  }
})
</script>

<style scoped lang="scss">
@import '@/styles/question-view-styles.scss';
</style>
